import { useState } from "react";
import { useLang, ensureSessionId } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import PreToolDisclaimer from "@/components/PreToolDisclaimer";

const KEYS = [
  "travel_dates_defined","passports_addressed","return_obligations_defined","residence_arrangements_defined",
  "communication_plan","school_calendar_addressed","transportation_addressed","expense_allocation",
  "emergency_communication","noncompliance_procedures","recognition_considered","judicial_approval_considered",
  "enforceability_reviewed","foreign_counsel_review","document_execution","translation_requirements",
  "logistical_implementation",
];
const OPTS = ["yes", "partial", "no", "unclear"];

export default function AgreementReadiness() {
  const { t, lang } = useLang();
  const [a, setA] = useState({});
  const [res, setRes] = useState(null);

  const submit = async () => {
    try {
      const r = await api.post("/agreement-readiness", { ...a, language: lang, session_id: ensureSessionId() });
      setRes(r.data.result); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error("Could not generate report."); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20" data-testid="agreement-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.agreement.title}</h1>
      <p className="mt-4 text-slate-300 max-w-3xl">{t.agreement.sub}</p>

      <PreToolDisclaimer showHague />

      {res ? (
        <div className="mt-10 card-mift p-6" data-testid="agreement-result">
          <h2 className="text-2xl font-semibold text-blue-300">{t.agreement.report_title}</h2>
          <p className="mt-3 text-sm text-slate-400 italic">{t.agreement.framing}</p>

          <div className="mt-6 grid gap-6 md:grid-cols-3">
            <ReportList icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                        title={t.agreement.addressed} items={res.addressed} tone="emerald" />
            <ReportList icon={<Clock className="w-4 h-4 text-amber-400" />}
                        title={t.agreement.partial} items={res.partial} tone="amber" />
            <ReportList icon={<AlertTriangle className="w-4 h-4 text-blue-300" />}
                        title={t.agreement.open} items={res.open_items} tone="blue" />
          </div>

          <p className="mt-8 text-xs text-slate-500 italic">{res.disclaimer}</p>
          <button className="mt-6 btn-ghost" onClick={() => { setRes(null); setA({}); }} data-testid="agreement-restart">
            {t.agreement.restart}
          </button>
        </div>
      ) : (
        <div className="mt-10 grid gap-4">
          {KEYS.map((k, i) => (
            <div key={k} className="card-mift p-5" data-testid={`ag-${k}`}>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Q{i + 1}</div>
              <div className="mt-1 text-slate-100 font-medium">{t.agreement.q[k]}</div>
              <div className="mt-4 max-w-md">
                <Select value={a[k] || ""} onValueChange={(v) => setA((s) => ({ ...s, [k]: v }))}>
                  <SelectTrigger data-testid={`ag-sel-${k}`} className="bg-[#0A1224] border-slate-700"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1224] border-slate-700 text-slate-100">
                    {OPTS.map((o) => <SelectItem key={o} value={o} data-testid={`ag-opt-${k}-${o}`}>{t.agreement.opts[o]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
          <button onClick={submit} disabled={KEYS.some((k) => !a[k])}
                  className="btn-primary w-max disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="agreement-submit">
            {t.agreement.submit}
          </button>
        </div>
      )}
    </div>
  );
}

function ReportList({ icon, title, items, tone }) {
  return (
    <div className="border border-slate-800 rounded-lg p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">{icon}{title}</div>
      <ul className="mt-3 space-y-1.5 text-sm text-slate-300">
        {items.length ? items.map((it, i) => <li key={i}>• {it}</li>) : <li className="text-slate-500 italic text-xs">—</li>}
      </ul>
    </div>
  );
}
